import { ChangeDetectorRef, Component, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, Location as AngularLocation } from '@angular/common';
import { AuthService } from 'src/app/services/auth-service/auth-service.service';
import { MatButtonModule } from '@angular/material/button';
import { BasePage } from 'src/app/shared/base-classes/base-page';
import { ActivatedRoute, Router } from '@angular/router';
import { AlbionImageService } from 'src/app/services/albion-image/albion-image.service';
import { AoDataGetterService } from 'src/app/services/ao-data-getter/ao-data-getter.service';
import { JsonReaderService } from 'src/app/services/json-reader/json-reader.service';
import { MarketResponse } from 'src/app/shared/models/ao-data-interfaces';
import { Item, ShopCategory } from 'src/app/shared/models/items.model';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { ItemPriceCardComponent } from 'src/app/shared/components/item-price-card/item-price-card.component';
import { ItemSelectorComponent } from 'src/app/shared/components/item-selector/item-selector.component';
import { LocationsLegendComponent } from 'src/app/shared/components/locations-legend/locations-legend.component';
import { UserSettingsService } from 'src/app/services/user-settings/user-settings.service';
import { MatIconModule } from '@angular/material/icon';
import { TextDividerComponent } from 'src/app/shared/components/text-divider/text-divider.component';
import { DialogComponent } from 'src/app/shared/components/dialog/dialog.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DEFAULT_PRICE_FAVORITE_LIST, PriceFavoritesList } from 'src/app/shared/models/price-favorites.model';
import { MAX_ITEMS_FAVORITE_LIST } from 'src/app/shared/constants/config.constants';
import { catchError, combineLatest, EMPTY, first } from 'rxjs';
import { getInternationalCsvSeparator, formatInternationalNumber, formatDateForExcel } from 'src/app/shared/constants/localization.constant';
import { LoadingBarService } from 'src/app/services/loading-bar/loading-bar.service';

@Component({
  selector: 'app-price-favorites',
  imports: [
    CommonModule,
    FormsModule,
    ItemSelectorComponent,
    MatCardModule,
    MatInputModule,
    ItemPriceCardComponent,
    MatFormFieldModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    LocationsLegendComponent,
    MatIconModule,
    MatButtonModule,
    TextDividerComponent,
    MatDialogModule,
    MatTooltipModule,
  ],
  templateUrl: './price-favorites.component.html',
  styleUrls: ['./price-favorites.component.scss'],
})
export class PriceFavoritesComponent extends BasePage {
  constructor(
    public authService: AuthService,
    private matDialog: MatDialog,
    private snackBar: MatSnackBar,
    route: ActivatedRoute,
    router: Router,
    urlLocation: AngularLocation,
    jsonReaderService: JsonReaderService,
    aoDataGetter: AoDataGetterService,
    albionImageService: AlbionImageService,
    userSettingsService: UserSettingsService,
    @Inject(PLATFORM_ID) platformId: Object,
    cdr: ChangeDetectorRef,
    public loadingBarService: LoadingBarService
  ) {
    super(route, router, urlLocation, jsonReaderService, aoDataGetter, albionImageService, userSettingsService, cdr, platformId);
  }

  selectedFavoriteList: PriceFavoritesList | null = null;

  createdFavoriteList: PriceFavoritesList | null = null;
  maxItems = MAX_ITEMS_FAVORITE_LIST;
  isEditting: boolean = false;

  override set selectedItem(item: Item | null) {
    if (item) {
      if (this.createdFavoriteList) {
        if (this.createdFavoriteList?.itemsIds.length < this.maxItems) {
          this.createdFavoriteList?.itemsIds.push(item.uniqueName);
        } else {
          this.snackBar.open(`You can't have more than ${this.maxItems} items in each Favorites Lists`, undefined, { duration: 2000 });
        }
      }
    }
  }

  filterItemsForSelect(items: Item[]): Item[] {
    return items;
  }

  filterShopCategoriesForSelect(categories: ShopCategory[]): ShopCategory[] {
    return categories;
  }

  filterItemsForPrices(items: Item[]): Item[] {
    if (this.selectedFavoriteList && this.items) {
      let filteredItems = this.items.filter((item) => this.selectedFavoriteList?.itemsIds.includes(item.uniqueName));

      // Remove duplicate items based on uniqueName
      filteredItems = filteredItems.filter((item, index, self) => self.findIndex((t) => t.uniqueName === item.uniqueName) === index);

      // Sort the filtered items
      filteredItems.sort((a, b) => {
        // Sort by shopCategory
        if (a.shopCategory !== b.shopCategory) {
          return (a.shopCategory?.name ?? '').localeCompare(b.shopCategory?.name ?? '');
        }
        // If shopCategory is the same, then sort by shopSubCategory
        if (a.shopSubCategory1 !== b.shopSubCategory1) {
          return (a.shopSubCategory1?.name ?? '').localeCompare(b.shopSubCategory1?.name ?? '');
        }
        // If shopSubCategory is the same, then sort by tier
        if (a.tier && b.tier && a.tier !== b.tier) {
          return a.tier - b.tier; // assuming tier is a number
        }
        // Finally, sort by enchantmentLevel
        return (a.enchantmentLevel ?? 0) - (b.enchantmentLevel ?? 0); // assuming enchantmentLevel is a number
      });

      return filteredItems;
    }
    return [];
  }

  filterPricesForCard(item: Item, prices: MarketResponse[] | null): MarketResponse[] | null {
    return prices ? prices.filter((x) => x.itemTypeId === item.uniqueName && x.qualityLevel.index <= (item.maxQualityLevel ?? 1) && this.selectedCities.includes(x.city)) : null;
  }

  selectedListChanged(event: any): void {
    this.selectedFavoriteList = event.value;
    if (this.items) {
      this.updatePrices(this.items);
      this.updateHistories(this.items);
    }
  }

  startCreatingList() {
    this.createdFavoriteList = JSON.parse(JSON.stringify(DEFAULT_PRICE_FAVORITE_LIST));
    this.selectedFavoriteList = null;
    this.cdr.markForCheck();
  }

  startEditingList() {
    this.isEditting = true;
    this.createdFavoriteList = JSON.parse(JSON.stringify(this.selectedFavoriteList));
    this.selectedFavoriteList = null;
    this.cdr.markForCheck();
  }

  cancelCreation() {
    if (this.isEditting) {
      this.selectedFavoriteList = this.userSettingsService.getUserPriceFavoritesLists().find((x) => x.name === this.createdFavoriteList?.name) ?? null;
      if (this.items) {
        this.updatePrices(this.items);
        this.updateHistories(this.items);
      }
    }
    this.createdFavoriteList = null;
    this.isEditting = false;
    this.cdr.markForCheck();
  }

  async saveCreatedList(): Promise<void> {
    if (this.createdFavoriteList) {
      await this.userSettingsService.addOrUpdateUserPriceFavoritesList(this.createdFavoriteList, this.isEditting);
    }
    this.isEditting = false;
    this.selectedFavoriteList = this.userSettingsService.getUserPriceFavoritesLists().find((x) => x.name === this.createdFavoriteList?.name) ?? null;
    if (this.items) {
      this.updatePrices(this.items);
      this.updateHistories(this.items);
    }
    this.createdFavoriteList = null;
    this.cdr.markForCheck();
  }

  async deleteList(): Promise<void> {
    const dialogRef = this.matDialog.open(DialogComponent, {
      width: '250px',
      data: { title: 'Confirm Deletion', message: `Are you sure you want to delete the list <strong>${this.selectedFavoriteList?.name}</strong>?` },
    });
    this.cdr.markForCheck();

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        if (this.selectedFavoriteList) {
          await this.userSettingsService.deleteUserPriceFavoritesList(this.selectedFavoriteList);
          this.selectedFavoriteList = null;
          this.cdr.markForCheck();
        }
      }
    });
  }

  removeListItem(uniqueName: string): void {
    const item = this.jsonReaderService.getItemFromIndex(uniqueName);

    const dialogRef = this.matDialog.open(DialogComponent, {
      width: '250px',
      data: { title: 'Confirm Deletion', message: `Are you sure you want to delete the item <strong>${item?.localizedName}</strong>?` },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        if (this.createdFavoriteList) {
          this.createdFavoriteList.itemsIds = this.createdFavoriteList.itemsIds.filter((x) => x != uniqueName);
          this.cdr.markForCheck();
        }
      }
    });
  }
  refreshPrices(): void {
    this.updatePrices(this.items ?? []);
    this.updateHistories(this.items ?? []);
  }

  private escapeCSV(str: string): string {
    return `"${str.replace(/"/g, '""')}"`;
  }

  exportPricesCsv(): void {
    if (!this.selectedFavoriteList) {
      console.warn('No favorite list selected');
      return;
    }

    combineLatest([this.prices$.pipe(first()), this.histories$.pipe(first())])
      .pipe(
        catchError((error) => {
          console.error('Error fetching data:', error);
          return EMPTY;
        })
      )
      .subscribe(([prices, histories]) => {
        if (!prices || !histories) {
          console.warn('No data available to export');
          return;
        }

        try {
          const rows = [
            ['Item Name', 'Location', 'Quality', 'Min Sell Price', 'Min Sell Date', 'Max Buy Price', 'Max Buy Date', `Average Price (${this.numberOfDaysForAverage} days)`, `Average Amount (${this.numberOfDaysForAverage} days)`].join(
              getInternationalCsvSeparator()
            ),
          ];

          this.filterItemsForPrices(this.items ?? []).forEach((item) => {
            const itemPrices = prices.filter((x) => x.itemTypeId === item.uniqueName && x.qualityLevel.index <= (item.maxQualityLevel ?? 1));

            itemPrices.forEach((price) => {
              const averages = this.aoDataGetter.getAveragePriceAndAmount(price.item, price.city, histories, price.qualityLevel, this.numberOfDaysForAverage);
              const row = [
                this.escapeCSV(item.localizedName),
                this.escapeCSV(price.city.displayName),
                price.qualityLevel.name,
                formatInternationalNumber(price.sellPriceMin),
                price.sellPriceMin ? formatDateForExcel(price.sellPriceMinDate) : '',
                formatInternationalNumber(price.buyPriceMax),
                price.buyPriceMax ? formatDateForExcel(price.buyPriceMaxDate) : '',
                formatInternationalNumber(averages.averagePrice),
                formatInternationalNumber(averages.averageAmount),
              ].join(getInternationalCsvSeparator());
              rows.push(row);
            });
          });

          const csvContent = rows.join('\n');
          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          const filename = `${this.selectedFavoriteList?.name}_prices_${new Date().toISOString().split('T')[0]}.csv`;

          link.setAttribute('href', url);
          link.setAttribute('download', filename);
          link.style.display = 'none';
          document.body.appendChild(link);

          link.click();

          // Cleanup
          setTimeout(() => {
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          }, 100);
        } catch (error) {
          console.error('Error generating CSV:', error);
        }
      });
  }
}
